import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCustomerById, addDeal, updateDeal, deleteDeal, getDealReceipt } from '../api/customerApi';
import { getProducts } from '../api/productApi';
import styles from './CustomerProfile.module.css';
import masaLogo from '../Assets/MasaLogo.png';

const CustomerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(null);

  const [isAddingDeal, setIsAddingDeal] = useState(false);
  const [isEditingDeal, setIsEditingDeal] = useState(null);

  // We store PDF as base64 in `receipt` if user picks a file
  const [dealData, setDealData] = useState({
    productId: '',
    productName: '',
    SKU: '',
    quantity: 1,
    sort: '',
    color: '',
    pricePerItem: 0,
    receipt: null,  // base64 string or null
  });

  const [products, setProducts] = useState([]);
  const [productsError, setProductsError] = useState(null);

  useEffect(() => {
    fetchCustomer();
    fetchProducts();
    // eslint-disable-next-line
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await getCustomerById(id);
      setCustomer(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch customer.');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response.data);
    } catch (err) {
      setProductsError(err.response?.data?.message || 'Failed to fetch products.');
    }
  };

  const handleProductChange = (e) => {
    const selectedProductId = e.target.value;
    const foundProduct = products.find((p) => p._id === selectedProductId);

    if (foundProduct) {
      setDealData({
        ...dealData,
        productId: foundProduct._id,
        productName: foundProduct.name,
        SKU: foundProduct.SKU,
        sort: '',
        color: '',
        quantity: 1,
        pricePerItem: 0,
        receipt: null,
      });
    } else {
      setDealData({
        ...dealData,
        productId: '',
        productName: '',
        SKU: '',
        sort: '',
        color: '',
        quantity: 1,
        pricePerItem: 0,
        receipt: null,
      });
    }
  };

  const handleSortChange = (e) => {
    setDealData((prev) => ({
      ...prev,
      sort: e.target.value,
      color: '',
    }));
  };

  const handleColorChange = (e) => {
    setDealData((prev) => ({
      ...prev,
      color: e.target.value,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDealData((prev) => ({ ...prev, [name]: value }));
  };
  // Somewhere inside your CustomerProfile component, define this new function:
  const handleViewReceipt = async (dealId) => {
    try {
      // 1) Fetch raw PDF bytes from the backend
      const response = await getDealReceipt(customer._id, dealId);

      // 2) Convert that ArrayBuffer to a Blob
      const file = new Blob([response.data], { type: 'application/pdf' });

      // 3) Create a local URL for that Blob
      const fileURL = URL.createObjectURL(file);

      // 4) Open in a new tab
      window.open(fileURL, '_blank');
    } catch (err) {
      console.error('Failed to fetch receipt:', err);
      setError('Unable to load receipt PDF.');
    }
  };

  // CHANGED: We'll read the file as base64 and store it in `dealData.receipt`
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setDealData((prev) => ({ ...prev, receipt: null }));
      return;
    }

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed!');
      e.target.value = null; // reset file input
      return;
    }
    
    // NEW BASE64 CODE: read the PDF as a base64 string
    const reader = new FileReader();
    reader.onload = (evt) => {
      // evt.target.result is the base64 string
      setDealData((prev) => ({
        ...prev,
        receipt: evt.target.result, 
      }));
    };
    reader.onerror = (evt) => {
      setError('Failed to read PDF file. Please try again.');
    };

    reader.readAsDataURL(file); // read as base64 data URL
  };

  const handleAddDeal = async (e) => {
    e.preventDefault();
    try {
      if (!dealData.productId) {
        return setError('Please select a product from the list.');
      }
      if (!dealData.sort) {
        return setError('Please select a sort.');
      }
      if (!dealData.color) {
        return setError('Please select a color.');
      }

      // We now pass the plain JS object `dealData` (which may have a base64 PDF in `receipt`)
      const response = await addDeal(id, dealData);
      setCustomer(response.data);
      setIsAddingDeal(false);

      // Reset form
      setDealData({
        productId: '',
        productName: '',
        SKU: '',
        quantity: 1,
        sort: '',
        color: '',
        pricePerItem: 0,
        receipt: dealData.receipt, // Only reset if a new file is uploaded
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add deal.');
    }
  };

    const handleUpdateDeal = async (e) => {
      e.preventDefault();
      try {
        if (!dealData.productId && !dealData.productName) {
          return setError('Please select a product or keep the existing product name.');
        }
        if (!dealData.sort) {
          return setError('Please select a sort.');
        }
        if (!dealData.color) {
          return setError('Please select a color.');
        }
    
        const response = await updateDeal(id, isEditingDeal, dealData);
        setCustomer(response.data);
        setIsEditingDeal(null);
    
        // Reset form while retaining existing productId and other fields
        setDealData({
          productId: dealData.productId, // Retain existing productId
          productName: dealData.productName,
          SKU: dealData.SKU,
          quantity: dealData.quantity,
          sort: dealData.sort,
          color: dealData.color,
          pricePerItem: dealData.pricePerItem,
          receipt: dealData.receipt, // Only reset if a new file is uploaded
        });
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to update deal.');
      }
    };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    try {
      await deleteDeal(id, dealId);
      setCustomer((prev) => ({
        ...prev,
        deals: prev.deals.filter((d) => d._id !== dealId),
      }));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete deal.');
    }
  };

  const handleCancelDeal = () => {
    setIsAddingDeal(false);
    setIsEditingDeal(null);
    setDealData({
      productId: '',
      productName: '',
      SKU: '',
      quantity: 1,
      sort: '',
      color: '',
      pricePerItem: 0,
      receipt: dealData.receipt, // Retain existing receipt
    });
    setError(null);
  };

  const selectedProduct = products.find((p) => p._id === dealData.productId);
  const availableSorts = selectedProduct?.sorts || [];
  const chosenSort = availableSorts.find((s) => s.sortName === dealData.sort);
  const availableColors = chosenSort?.colors || [];

  return (
    <div className={styles.customerProfileContainer}>
      <header className={styles.header}>
        <img src={masaLogo} alt="Dashboard Logo" className={styles.logo} />
        <h1 className={styles.pageTitle}>Customer Profile</h1>
      </header>

      <main className={styles.mainContent}>
        <button className={styles.backButton} onClick={() => navigate('/customers')}>
          ← Back to Customers
        </button>

        {error && <p className={styles.errorMessage}>{error}</p>}
        {productsError && <p className={styles.errorMessage}>{productsError}</p>}

        {customer ? (
          <div className={styles.profileDetails}>
            <h2>
              {customer.name} (ID: {customer.customerID})
            </h2>
            <button
              className={styles.addDealButton}
              onClick={() => setIsAddingDeal(true)}
            >
              + Add New Deal
            </button>

            {/* Deals List */}
            <div className={styles.dealsList}>
              <h3>Deals</h3>
              {customer.deals.length === 0 ? (
                <p>No deals found.</p>
              ) : (
                <table className={styles.dealsTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>_id</th>
                      <th>Product Name</th>
                      <th>SKU</th>
                      <th>Quantity</th>
                      <th>Sort</th>
                      <th>Color</th>
                      <th>Price/Item</th>
                      <th>Receipt</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.deals.map((deal) => (
                      <tr key={deal._id}>
                        <td>{new Date(deal.createdAt).toLocaleDateString()}</td>
                        <td>{deal._id}</td>
                        <td>{deal.productName}</td>
                        <td>{deal.SKU}</td>
                        <td>{deal.quantity}</td>
                        <td>{deal.sort}</td>
                        <td>{deal.color}</td>
                        <td>${deal.pricePerItem.toFixed(2)}</td>
                        <td>
                          {deal.receipt ? (
                            <button onClick={() => handleViewReceipt(deal._id)}>
                              View Receipt
                            </button>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>
                        <button
                            className={styles.editDealButton}
                            onClick={() => {
                              setIsEditingDeal(deal._id);
                              setDealData({
                                productId: deal.productId || '', // Retain existing productId
                                productName: deal.productName,
                                SKU: deal.SKU,
                                quantity: deal.quantity,
                                sort: deal.sort,
                                color: deal.color,
                                pricePerItem: deal.pricePerItem,
                                receipt: deal.receipt, // Only update if a new file is uploaded
                              });
                            }}
                          >
                            Edit
                          </button>

                          <button
                            className={styles.deleteDealButton}
                            onClick={() => handleDeleteDeal(deal._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Add Deal Modal */}
            {isAddingDeal && (
              <div className={styles.modalBackdrop}>
                <div className={styles.modalContent}>
                  <h2>Add New Deal</h2>
                  <form onSubmit={handleAddDeal} className={styles.dealForm}>
                    <label>Product:</label>
                    <select
                      value={dealData.productId}
                      onChange={handleProductChange}
                      className={styles.formInput}
                      required
                    >
                      <option value="">-- Select Product --</option>
                      {products.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.name} (SKU: {p.SKU})
                        </option>
                      ))}
                    </select>

                    <label>Sort:</label>
                    <select
                      value={dealData.sort}
                      onChange={handleSortChange}
                      className={styles.formInput}
                      required
                    >
                      <option value="">-- Select Sort --</option>
                      {availableSorts.map((s) => (
                        <option key={s.sortName} value={s.sortName}>
                          {s.sortName}
                        </option>
                      ))}
                    </select>

                    <label>Color:</label>
                    <select
                      value={dealData.color}
                      onChange={handleColorChange}
                      className={styles.formInput}
                      required
                    >
                      <option value="">-- Select Color --</option>
                      {chosenSort?.colors.map((cObj) => (
                        <option key={cObj.color} value={cObj.color}>
                          {cObj.color}
                        </option>
                      ))}
                    </select>

                    <label>Quantity:</label>
                    <input
                      type="number"
                      name="quantity"
                      value={dealData.quantity}
                      onChange={handleInputChange}
                      min="1"
                      required
                      className={styles.formInput}
                    />

                    <label>Price Per Item ($):</label>
                    <input
                      type="number"
                      name="pricePerItem"
                      value={dealData.pricePerItem}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      required
                      className={styles.formInput}
                    />

                    <label>Upload Receipt (PDF):</label>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      className={styles.fileInput}
                    />

                    <div className={styles.buttonGroup}>
                      <button type="submit" className={styles.confirmButton}>
                        Add Deal
                      </button>
                      <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleCancelDeal}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit Deal Modal */}
            {isEditingDeal && (
              <div className={styles.modalBackdrop}>
                <div className={styles.modalContent}>
                  <h2>Edit Deal</h2>
                  <form onSubmit={handleUpdateDeal} className={styles.dealForm}>
                    <label>Product:</label>
                    <select
                      value={dealData.productId}
                      onChange={handleProductChange}
                      className={styles.formInput}
                    >
                      <option value="">-- Select Product --</option>
                      {products.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.name} (SKU: {p.SKU})
                        </option>
                      ))}
                    </select>

                    <label>Sort:</label>
                    <select
                      value={dealData.sort}
                      onChange={handleSortChange}
                      className={styles.formInput}
                    >
                      <option value="">-- Select Sort --</option>
                      {availableSorts.map((s) => (
                        <option key={s.sortName} value={s.sortName}>
                          {s.sortName}
                        </option>
                      ))}
                    </select>

                    <label>Color:</label>
                    <select
                      value={dealData.color}
                      onChange={handleColorChange}
                      className={styles.formInput}
                    >
                      <option value="">-- Select Color --</option>
                      {chosenSort?.colors.map((cObj) => (
                        <option key={cObj.color} value={cObj.color}>
                          {cObj.color}
                        </option>
                      ))}
                    </select>

                    <label>Quantity:</label>
                    <input
                      type="number"
                      name="quantity"
                      value={dealData.quantity}
                      onChange={handleInputChange}
                      min="1"
                      className={styles.formInput}
                      required
                    />

                    <label>Price Per Item ($):</label>
                    <input
                      type="number"
                      name="pricePerItem"
                      value={dealData.pricePerItem}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className={styles.formInput}
                      required
                    />

                    <label>Upload Receipt (PDF):</label>
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      className={styles.fileInput}
                    />

                    <div className={styles.buttonGroup}>
                      <button type="submit" className={styles.confirmButton}>
                        Save Changes
                      </button>
                      <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleCancelDeal}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p>Loading customer data...</p>
        )}
      </main>
    </div>
  );
};

export default CustomerProfile;
